import {Source, WeatherColumns} from 'src/lib/api/series';

/**
 * The dynamical.org datasets hf-web can read directly, keyed by the HF API source
 * they replace. Grid geometry, chunk shapes, and lead-time grids are read from the
 * store's zarr metadata at open time; only identity and semantics live here.
 */
export type DynamicalDatasetDescriptor = {
  source: Source;
  /** STAC collection id at https://stac.dynamical.org/{datasetId}/collection.json */
  datasetId: string;
  /**
   * Geographic grids have 1-D latitude/longitude coordinates in degrees.
   * Projected grids (HRRR) have 1-D x/y coordinates in meters plus a spatial_ref
   * describing the CRS; basin geometries must be projected before rasterization.
   */
  gridKind: 'geographic' | 'projected';
  /** True when the store has an ensemble_member dimension to average over. */
  hasEnsemble: boolean;
  /** Store variable name for each external column the dashboard can request. */
  storeVariableByColumn: {[column in WeatherColumns]: string};
  /**
   * How precipitation steps are labeled, mirroring the API's grib ingestion so
   * direct reads line up timestamp-for-timestamp:
   * - 'interval-end' (GEFS/GFS): a step's rate covers the interval *ending* at
   *   its lead — the store's native convention. Lead 0 carries no rate and is
   *   dropped, matching series that start at lead 1/3.
   * - 'interval-start' (HRRR): the API labels each hour's accumulation at the
   *   interval *start* (hrrr.py shifts precip up one hour and drops the last
   *   row), so lead 0 is kept, precipitation comes from the next store lead,
   *   and the final store lead is dropped.
   */
  precipTiming: 'interval-end' | 'interval-start';
  /**
   * Largest lead the HF API serves for this source. dynamical.org's GFS store
   * extends to 384 h but the API's ingestion stops at 240 h; serving the extra
   * days would silently change forecast length per data path.
   */
  maxLeadHours?: number;
  /**
   * How the API's H4 ingestion samples this grid, applied when reading for H4
   * sites so direct values are indistinguishable from the precomputed series the
   * API serves today (verified pixel-by-pixel against live API responses):
   * - GEFS: the grib path reads NOAA's 0.5° product for every lead
   *   (`assert reader.transform == D50_TRANSFORM` in _gefs/current.py) — every
   *   other cell of dynamical's center-registered 0.25° grid.
   * - GFS: the tile path registers each grid value to the quarter-degree square
   *   NE of it (gfs_noaa.py tile bounds skip the half-pixel latitude shift), so
   *   effective cell centers sit +0.125° N/E of the store's.
   * H5 sites read the store natively (the H5 GEFS path is itself a 0.25°
   * dynamical read); omitting this field means the grids already agree.
   */
  h4Sampling?: {stride?: number; offsetY?: number; offsetX?: number};
  /**
   * Store URL used when the STAC catalog is unreachable. A stale pinned version
   * keeps working until dynamical.org deletes the old store; the API fallback
   * covers everything else.
   */
  fallbackStoreUrl: string;
};

const WEATHER_STORE_VARIABLES: {[column in WeatherColumns]: string} = {
  [WeatherColumns.TEMPERATURE]: 'temperature_2m',
  [WeatherColumns.PRECIPITATION]: 'precipitation_surface',
};

export const DYNAMICAL_DATASETS: {[source: string]: DynamicalDatasetDescriptor} = {
  [Source.GEFS]: {
    source: Source.GEFS,
    datasetId: 'noaa-gefs-forecast-35-day',
    gridKind: 'geographic',
    hasEnsemble: true,
    storeVariableByColumn: WEATHER_STORE_VARIABLES,
    precipTiming: 'interval-end',
    h4Sampling: {stride: 2},
    fallbackStoreUrl:
      'https://dynamical-noaa-gefs.s3.us-west-2.amazonaws.com/noaa-gefs-forecast-35-day/v0.2.0.icechunk',
  },
  [Source.GFS]: {
    source: Source.GFS,
    datasetId: 'noaa-gfs-forecast',
    gridKind: 'geographic',
    hasEnsemble: false,
    storeVariableByColumn: WEATHER_STORE_VARIABLES,
    precipTiming: 'interval-end',
    maxLeadHours: 240,
    h4Sampling: {offsetY: 0.125, offsetX: 0.125},
    fallbackStoreUrl:
      'https://dynamical-noaa-gfs.s3.us-west-2.amazonaws.com/noaa-gfs-forecast/v0.2.7.icechunk',
  },
  [Source.HRRR]: {
    source: Source.HRRR,
    datasetId: 'noaa-hrrr-forecast-48-hour',
    gridKind: 'projected',
    hasEnsemble: false,
    storeVariableByColumn: WEATHER_STORE_VARIABLES,
    precipTiming: 'interval-start',
    fallbackStoreUrl:
      'https://dynamical-noaa-hrrr.s3.us-west-2.amazonaws.com/noaa-hrrr-forecast-48-hour/v0.1.0.icechunk',
  },
};

export function isDynamicalSource(source: Source): boolean {
  return source in DYNAMICAL_DATASETS;
}

/**
 * Which API pipeline's sampling semantics a read should reproduce. H4 sites
 * (no site.geometry — their weather comes from precomputed series) use the
 * ingestion grids above plus exact-cell point sampling without cos-latitude
 * weighting; H5 sites read the store natively, matching the H5 GEFS path.
 */
export type GridSampling = 'native' | 'h4';
